import { internalUsersClient, usersClient } from 'api/AdgangClients';
import { Button, ConfirmationModal } from 'common.ui';
import Page from 'components/page/Page';
import React, { useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

const DeleteUserScreen = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const location = useLocation();
  const history = useHistory();

  const [show, setShow] = useState(false);

  const returnPath = useMemo(() => {
    const segments = location.pathname.split('/').filter((x) => !!x);

    if (segments.length === 0) {
      return '/';
    }

    return `/${segments[0]}`;
  }, [location.pathname]);

  const isExternalUser = returnPath === '/externalusers';

  // Fetch internal user first (only if not external user)
  const { data: internalUser } = useQuery({
    queryKey: ['internalUser', id],
    queryFn: () => internalUsersClient.apiInternalUsersInternalUserIdGet({ internalUserId: id }),
    enabled: !isExternalUser
  });

  const userId = useMemo(() => {
    if (isExternalUser) {
      return id;
    }

    return internalUser?.userId ?? '';
  }, [id, internalUser?.userId, isExternalUser]);

  // Fetch user using the correct userId (from internalUser if internal user, otherwise id)
  const { data: user } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersClient.apiUsersUserIdGet({ userId }),
    enabled: !!userId
  });

  // Mutation for deleting internal user
  const deleteInternalUserMutation = useMutation({
    mutationFn: (internalUserId: string) =>
      internalUsersClient.apiInternalUsersInternalUserIdDelete({
        internalUserId,
        deleteB2CUser: true
      }),
    onSuccess: () => {
      setShow(false);
      history.push(returnPath);
    },
    onError: (error: unknown) => {
      alert(error instanceof Error ? error.message : String(error));
    }
  });

  // Mutation for deleting user
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => usersClient.apiUsersUserIdDelete({ userId }),
    onSuccess: () => {
      setShow(false);
      history.push(returnPath);
    },
    onError: (error: unknown) => {
      alert(error instanceof Error ? error.message : String(error));
    }
  });

  const isDeleting = deleteInternalUserMutation.isLoading || deleteUserMutation.isLoading;

  return (
    <Page header='Slett bruker' title={['Slett bruker', id]}>
      <p>
        <em>Hvis du ikke er SysAdmin, burde du ikke være her</em>
      </p>

      <section className='content-box'>
        <dl>
          <dt>Navn:</dt>
          <dd>{`${user?.firstName ?? internalUser?.givenName} ${user?.surname ?? internalUser?.surname}`}</dd>

          <dt>Epost:</dt>
          <dd>{`${user?.email ?? internalUser?.mail}`}</dd>
        </dl>
      </section>

      <section className='content-box'>
        <pre>{JSON.stringify({ user, internalUser }, null, 2)}</pre>
      </section>

      <Button text='Slett bruker' onClick={() => setShow(true)} />

      <ConfirmationModal
        header={isDeleting ? 'SLETTER BRUKER...' : 'Er du sikker på at du vil slette denne brukeren?'}
        show={show}
        onCancel={() => {
          if (isDeleting) {
            return;
          }

          setShow(false);
        }}
        onAccept={() => {
          if (isDeleting) {
            return;
          }

          // Ingen UserId, mest sannsynlig en "hengende" InternalUser
          if (!userId) {
            if (!internalUser) {
              // Feilen vises til brukeren via alert-dialogen under, ingen logging av persondata her
              alert(
                'Kan ikke utføre sletting, her må du ta et dypere dykk for å finne ut hva som feiler brukeren!'
              );
              return;
            }

            deleteInternalUserMutation.mutate(internalUser.internalUserId!);
            return;
          }

          deleteUserMutation.mutate(userId);
        }}
      />
    </Page>
  );
};

export default DeleteUserScreen;
